import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Star, LogOut, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import BottomNav from "@/components/BottomNav";
import { useAuth, API } from "@/App";

const AboutPage = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });

  // Determine user type from URL
  const userType = location.pathname.includes("/brand/") ? "brand" : "influencer";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully!");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-nav">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#87CEEB]/20 flex items-center justify-center">
              <Star className="w-4 h-4 text-[#FF69B4]" />
            </div>
            <span className="font-outfit font-bold text-lg text-[#1E293B]">About</span>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleLogout}
            className="text-[#64748B] hover:text-[#FF69B4]"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Moto Section */}
        <div className="bg-white rounded-2xl p-6 md:p-8 mb-6 shadow-sm">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-[#87CEEB]/20 flex items-center justify-center mx-auto mb-4">
              <Star className="w-8 h-8 text-[#FF69B4]" />
            </div>
            <h1 className="font-outfit font-bold text-2xl md:text-3xl text-[#1E293B] mb-3">
              FOUNDSTAR
            </h1>
          </div>
          
          <div className="space-y-4 text-center">
            <p className="font-outfit text-lg md:text-xl text-[#1E293B]">
              "Every creator deserves a chance to shine."
            </p>
          </div>
        </div>

        {/* Our Mission */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
          <h2 className="font-outfit font-semibold text-xl text-[#1E293B] mb-4">Our Mission</h2>
          <div className="space-y-4 text-[#64748B]">
            <p>
              FOUNDSTAR was founded with a simple vision: to create a trusted marketplace where creators can monetize their influence and brands can find authentic partnerships that deliver real value.
            </p>
            <p>
              We noticed a gap in the influencer marketing industry — too many fake followers, inflated metrics, and broken promises. We built FOUNDSTAR to solve these problems through verification, transparency, and a creator-first approach.
            </p>
            <p>
              Today, we're proud to be the platform of choice for thousands of verified creators and hundreds of brands who value quality partnerships over empty numbers.
            </p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="font-outfit font-semibold text-xl text-[#1E293B] mb-4">Contact Us</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[#1E293B]">Your Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="mt-1 rounded-xl"
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <Label className="text-[#1E293B]">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="mt-1 rounded-xl"
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <Label className="text-[#1E293B]">Message</Label>
              <Textarea
                placeholder="How can we help you?"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                required
                className="mt-1 rounded-xl min-h-[120px]"
                data-testid="contact-message-input"
              />
            </div>
            <Button 
              type="submit"
              disabled={sending}
              className="w-full bg-[#FF69B4] hover:bg-[#E05297] text-white rounded-full py-6"
              data-testid="contact-submit-btn"
            >
              {sending ? "Sending..." : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </form>

          {/* Official Email */}
          <div className="mt-6 pt-6 border-t border-[#E2E8F0] text-center">
            <p className="text-sm text-[#64748B] mb-3">Or reach us directly</p>
            
            {/* Social Links */}
            <div className="flex justify-center gap-4 mb-4">
              <a 
                href="https://wa.me/919579066355" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors text-sm font-medium"
                data-testid="about-whatsapp-link"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </a>
              <a 
                href="https://www.instagram.com/foundstar_" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white rounded-full hover:opacity-90 transition-opacity text-sm font-medium"
                data-testid="about-instagram-link"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                @foundstar_
              </a>
            </div>
            
            <a 
              href="mailto:support@foundstar.in" 
              className="inline-flex items-center gap-2 text-[#FF69B4] font-medium hover:underline"
              data-testid="contact-email-link"
            >
              <Mail className="w-4 h-4" />
              support@foundstar.in
            </a>
          </div>
        </div>
      </main>

      <BottomNav userType={userType} />
    </div>
  );
};

export default AboutPage;
